import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Loader2, FolderOpen, ChevronDown, X } from 'lucide-react';
import { toast } from 'sonner';
import {
  cloudSourceService,
  CloudSourceListRow,
} from '@/services/cloudSourceService';
import { logger } from '@/utils/logger';
import { getFriendlyErrorMessage } from '@/utils/errorMessage';
import FolderPickerDialog from './FolderPickerDialog';

interface ConfigModalProps {
  source: CloudSourceListRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void | Promise<void>;
  mandatory?: boolean;
}

// Mirrors orgain/kb/integrations/cloud_sources/sync.py:_ALLOWED_FILE_TYPES
// minus "other" - those are the extensions discovery actually downloads.
// The dropdown only offers these, so an unsupported value can't be picked.
export const SUPPORTED_EXTENSIONS = [
  'pdf', 'docx', 'doc', 'txt', 'md', 'rtf',
  'xlsx', 'xls', 'csv', 'pptx', 'ppt',
  'png', 'jpg', 'jpeg', 'gif', 'tiff', 'bmp',
];

// file_extension_filter is stored as a comma-separated string (e.g.
// ".pdf,.docx"); the backend re-normalises on save. In the UI we work with
// bare, lower-cased extensions.
const parseExtensions = (raw?: string | null): string[] =>
  (raw || '')
    .split(',')
    .map((s) => s.trim().toLowerCase().replace(/^\./, ''))
    .filter(Boolean);

/**
 * Tiny config form per the plan: folder list + extension filter. Anything
 * else (max file size, batch size, redaction policy) is global and not
 * exposed per-source.
 */
const CloudSourceConfigModal: React.FC<ConfigModalProps> = ({
  source,
  open,
  onOpenChange,
  onSaved,
  mandatory = false,
}) => {
  const isDrive = source.provider === 'Google Drive';
  const isConnected = source.connection_status === 'Connected';

  const [folderList, setFolderList] = useState(
    mandatory
      ? ''
      : isDrive ? source.drive_folder_ids || '' : source.sharepoint_folder_paths || '',
  );
  const [selectedExtensions, setSelectedExtensions] = useState<string[]>(
    () => (mandatory ? [] : parseExtensions(source.file_extension_filter)),
  );
  // Doctype default is 1 (included) - treat an absent value as checked.
  const [includeSubfolders, setIncludeSubfolders] = useState(
    source.include_subfolders !== 0,
  );
  const [saving, setSaving] = useState(false);
  // FolderPickerDialog toggle - the dialog itself owns the API calls
  // and selection state, then writes back via onConfirm.
  const [pickerOpen, setPickerOpen] = useState(false);

  const toggleExtension = (ext: string) => {
    setSelectedExtensions((prev) =>
      prev.includes(ext) ? prev.filter((e) => e !== ext) : [...prev, ext],
    );
  };

  // folderList is a comma-separated string; split into chips for display.
  const folderChips = folderList
    .split(',')
    .map((f) => f.trim())
    .filter(Boolean);

  const removeFolder = (folder: string) => {
    setFolderList(folderChips.filter((f) => f !== folder).join(', '));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: Parameters<typeof cloudSourceService.saveSource>[0] = {
        source: source.name,
        file_extension_filter: selectedExtensions.map((e) => `.${e}`).join(','),
        include_subfolders: includeSubfolders ? 1 : 0,
      };
      if (isDrive) {
        payload.drive_folder_ids = folderList.trim();
      } else {
        payload.sharepoint_folder_paths = folderList.trim();
      }
      payload.confirm_whole_drive_scan = folderList.trim() ? 0 : 1;
      const res = await cloudSourceService.saveSource(payload);
      // Scope change purges the queued backlog for folders no longer in
      // scope (backend save_source -> purge_out_of_scope_pending). Say so -
      // otherwise a large pending counter silently dropping to zero looks
      // like a bug, not a feature.
      if ((res.cleared_pending ?? 0) > 0) {
        toast.success(
          `Settings saved. ${res.cleared_pending} queued file${
            res.cleared_pending === 1 ? '' : 's'
          } from the previous folder scope ${
            res.cleared_pending === 1 ? 'was' : 'were'
          } cleared.`,
        );
      } else {
        toast.success('Settings saved. Changes will be applied during the next sync.');
      }
      await onSaved();
      onOpenChange(false);
    } catch (err: unknown) {
      const msg = getFriendlyErrorMessage(err, 'Save failed');
      logger.error('Save source failed', err);
      toast.error('Save failed', { description: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!saving && !(mandatory && !o)) onOpenChange(o);
      }}
    >
      <DialogContent
        className="dark bg-background text-foreground sm:max-w-md"
        hideCloseButton={mandatory}
        onInteractOutside={mandatory ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={mandatory ? (e) => e.preventDefault() : undefined}
      >
        <DialogHeader>
          <DialogTitle>Configure {source.provider}</DialogTitle>
          <DialogDescription>
            Changes take effect on the next sync.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div>
            <Label>Folders</Label>
            <div
              role="button"
              tabIndex={isConnected ? 0 : -1}
              aria-disabled={!isConnected}
              onClick={() => isConnected && setPickerOpen(true)}
              onKeyDown={(e) => {
                if (isConnected && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  setPickerOpen(true);
                }
              }}
              className={`mt-1 flex min-h-10 w-full items-center gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                isConnected ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
              }`}
            >
              <div className="flex flex-1 flex-wrap items-center gap-1">
                {folderChips.length ? (
                  folderChips.map((f) => (
                    <Badge
                      key={f}
                      variant="secondary"
                      className="gap-1 pr-1 font-normal"
                    >
                      {f}
                      <button
                        type="button"
                        aria-label={`Remove ${f}`}
                        onClick={(ev) => {
                          ev.stopPropagation();
                          removeFolder(f);
                        }}
                        className="rounded-sm opacity-60 hover:bg-black/10 hover:opacity-100"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))
                ) : (
                  <span className="text-muted-foreground">
                    {isDrive
                      ? 'Whole account will be scanned'
                      : 'Whole drive will be scanned'}
                  </span>
                )}
              </div>
              <span className="flex shrink-0 items-center gap-1 text-muted-foreground">
                <FolderOpen className="h-4 w-4" />
                Browse
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="include_subfolders"
              checked={includeSubfolders}
              onCheckedChange={(c) => setIncludeSubfolders(c === true)}
            />
            <Label
              htmlFor="include_subfolders"
              className="cursor-pointer font-normal"
            >
              Include subfolders
            </Label>
          </div>

          <div>
            <Label>File extension filter</Label>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <div
                  role="button"
                  tabIndex={0}
                  className="mt-1 flex min-h-10 w-full cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <div className="flex flex-1 flex-wrap items-center gap-1">
                    {selectedExtensions.length ? (
                      selectedExtensions.map((e) => (
                        <Badge
                          key={e}
                          variant="secondary"
                          className="gap-1 pr-1 font-normal"
                        >
                          .{e}
                          <button
                            type="button"
                            aria-label={`Remove .${e}`}
                            onPointerDown={(ev) => ev.stopPropagation()}
                            onClick={(ev) => {
                              ev.stopPropagation();
                              toggleExtension(e);
                            }}
                            className="rounded-sm opacity-60 hover:bg-black/10 hover:opacity-100"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))
                    ) : (
                      <span className="text-muted-foreground">
                        Use workspace default
                      </span>
                    )}
                  </div>
                  <ChevronDown className="h-4 w-4 shrink-0 opacity-60" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="dark bg-background text-foreground max-h-64 w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto"
              >
                {SUPPORTED_EXTENSIONS.map((ext) => (
                  <DropdownMenuCheckboxItem
                    key={ext}
                    checked={selectedExtensions.includes(ext)}
                    onCheckedChange={() => toggleExtension(ext)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    .{ext}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            {selectedExtensions.length > 0 && (
              <button
                type="button"
                onClick={() => setSelectedExtensions([])}
                className="mt-1 text-xs text-red-400 underline hover:text-red-300"
              >
                Clear all ({selectedExtensions.length})
              </button>
            )}
          </div>
        </div>

        <DialogFooter>
          {!mandatory && (
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
              Cancel
            </Button>
          )}
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>

      {pickerOpen && (
        <FolderPickerDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          sourceName={source.name}
          provider={source.provider}
          initialPaths={folderList}
          onConfirm={(paths) => {
            setFolderList(paths);
          }}
        />
      )}
    </Dialog>
  );
};

export default CloudSourceConfigModal;
